import React, { forwardRef, useState } from 'react';
import '../../styles/previewdetails.css';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';

const PreviewDetailsPage = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { formData } = props;
    const [showPreview, setShowPreview] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [currentFileUrl, setCurrentFileUrl] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);

    // Function to get ID card type label
    const getIdCardTypeLabel = (type) => {
        const idCardTypes = {
            'CF': 'CF',
            'CJ': 'CJ',
            'DIMEX': 'DIMEX',
            'NITE': 'NITE',
            'PE': 'PE',
            'COF': 'COF'
        };
        return idCardTypes[type] || type;
    };

    // Helper function to display value or "Not Provided"
    const displayValue = (value, capitalize = false) => {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            return <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>;
        }
        return <span className={capitalize ? 'capitalize' : ''}>{value}</span>;
    };

    // Function to handle file click - opens in modal
    const handleFileClick = (file) => {
        const fileUrl = URL.createObjectURL(file);
        setCurrentFile(file);
        setCurrentFileUrl(fileUrl);
        setShowPreview(true);
        setZoomLevel(1);
    };

    // Function to close modal and clean up
    const handleClosePreview = () => {
        setShowPreview(false);
        if (currentFileUrl) {
            URL.revokeObjectURL(currentFileUrl);
            setCurrentFileUrl(null);
        }
        setCurrentFile(null);
        setZoomLevel(1);
    };

    // Zoom functions
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleZoomReset = () => {
        setZoomLevel(1);
    };

    // Function to get file type and appropriate icon
    const getFileInfo = (fileName) => {
        const extension = fileName?.split('.').pop().toLowerCase();
        let icon, type;

        switch (extension) {
            case 'pdf':
                icon = <i className="fas fa-file-pdf file-icon pdf-icon"></i>;
                type = 'PDF';
                break;
            case 'jpg':
            case 'jpeg':
                icon = <i className="fas fa-file-image file-icon image-icon"></i>;
                type = 'JPEG Image';
                break;
            case 'png':
                icon = <i className="fas fa-file-image file-icon image-icon"></i>;
                type = 'PNG Image';
                break;
            case 'gif':
                icon = <i className="fas fa-file-image file-icon image-icon"></i>;
                type = 'GIF Image';
                break;
            default:
                icon = <i className="fas fa-file file-icon default-icon"></i>;
                type = 'Document';
        }

        return { icon, type };
    };

    // Check if file is an image
    const isImageFile = (fileName) => {
        const extension = fileName?.toLowerCase();
        return extension.endsWith('.jpg') || extension.endsWith('.jpeg') ||
            extension.endsWith('.png') || extension.endsWith('.gif');
    };

    // Function to render file content in modal
    const renderFileContent = () => {
        if (!currentFile || !currentFileUrl) return null;

        const fileName = currentFile.name.toLowerCase();

        if (fileName.endsWith('.pdf')) {
            return (
                <embed
                    src={currentFileUrl}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ minHeight: '70vh', border: 'none' }}
                    title={currentFile.name}
                />
            );
        } else if (isImageFile(fileName)) {
            return (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    background: '#f8f9fa',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        display: 'flex',
                        gap: '8px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 253, 253, 0.95) 100%)',
                        padding: '10px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 16px rgba(101, 199, 194, 0.3)',
                        zIndex: 10,
                        border: '1px solid rgba(101, 199, 194, 0.2)'
                    }}>
                        <button
                            className="zoom-btn"
                            onClick={handleZoomOut}
                            title="Zoom Out"
                            disabled={zoomLevel <= 0.5}
                            style={{
                                width: '38px',
                                height: '38px',
                                border: '2px solid #e0e6ed',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfd 100%)',
                                borderRadius: '6px',
                                cursor: zoomLevel <= 0.5 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                color: '#2c3e50',
                                opacity: zoomLevel <= 0.5 ? 0.4 : 1
                            }}
                        >
                            <i className="fas fa-minus"></i>
                        </button>
                        <div style={{
                            padding: '0 16px',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#65c7c2',
                            minWidth: '60px',
                            justifyContent: 'center'
                        }}>
                            {Math.round(zoomLevel * 100)}%
                        </div>
                        <button
                            className="zoom-btn"
                            onClick={handleZoomIn}
                            title="Zoom In"
                            disabled={zoomLevel >= 3}
                            style={{
                                width: '38px',
                                height: '38px',
                                border: '2px solid #e0e6ed',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfd 100%)',
                                borderRadius: '6px',
                                cursor: zoomLevel >= 3 ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                color: '#2c3e50',
                                opacity: zoomLevel >= 3 ? 0.4 : 1
                            }}
                        >
                            <i className="fas fa-plus"></i>
                        </button>
                        <button
                            className="zoom-btn"
                            onClick={handleZoomReset}
                            title="Reset Zoom"
                            style={{
                                width: '38px',
                                height: '38px',
                                border: '2px solid #e0e6ed',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfd 100%)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                color: '#2c3e50'
                            }}
                        >
                            <i className="fas fa-undo"></i>
                        </button>
                    </div>
                    <div style={{
                        flex: 1,
                        overflow: 'auto',
                        position: 'relative',
                        minHeight: 0
                    }}>
                        <div style={{
                            display: 'block',
                            padding: '20px',
                            minHeight: '100%'
                        }}>
                            <div style={{
                                display: 'inline-block',
                                transition: 'transform 0.3s ease',
                                transformOrigin: 'top left',
                                position: 'relative',
                                transform: `scale(${zoomLevel})`
                            }}>
                                <img
                                    src={currentFileUrl}
                                    alt={currentFile.name}
                                    style={{
                                        display: 'block',
                                        maxWidth: '85vw',
                                        maxHeight: '75vh',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-file fa-4x default-icon" style={{ marginBottom: '20px' }}></i>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        {t('previewDetails.labels.cannotPreviewFile')}
                    </p>
                    <a
                        href={currentFileUrl}
                        download={currentFile.name}
                        className="btn btn-primary"
                    >
                        <i className="fas fa-download"></i> Download File
                    </a>
                </div>
            );
        }
    };

    return (
        <div ref={ref}>
            <div className="preview-container">
                <h2 className="section-title">{t('previewDetails.sections.registration')}</h2>
                <div className="details-card">
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.name')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.name, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('vendorRegistration.formLabels.adminName')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.admin_name, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('vendorRegistration.formLabels.userName')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.user_name)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.email')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.email)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.phone')}</p>
                            <p className="field-value">
                                {formData.registrationDetails.phone
                                    ? `${formData.registrationDetails.phone_country.dialCode} ${formData.registrationDetails.phone}`
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.emergencyPhone')}</p>
                            <p className="field-value">
                                {formData.registrationDetails.emergency_phone
                                    ? `${formData.registrationDetails.emergency_country.dialCode} ${formData.registrationDetails.emergency_phone}`
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.password')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.password)}</p>
                        </div>
                    </div>
                </div>

                <h2 className="section-title">{t('previewDetails.sections.vendorDetails')}</h2>
                <div className="details-card">
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.vendorType')}</p>
                            <p className="field-value">
                                {formData.vendorType?.type
                                    ? (formData.vendorType?.type === "other"
                                        ? t(`professionalsList.${formData.vendorType.professional}`)
                                        : t(`professionalsList.${formData.vendorType?.type}`))
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.accreditation')}</p>
                            <p className="field-value">{displayValue(formData.vendorDetails.accreditation)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.registerNumber')}</p>
                            <p className="field-value">{displayValue(formData.vendorDetails.reg_number)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.website')}</p>
                            <p className="field-value">{displayValue(formData.vendorDetails.website)}</p>
                        </div>
                    </div>
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.establishedYear')}</p>
                            <p className="field-value">{displayValue(formData.vendorDetails.established_year)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.sections.subscription')}</p>
                            {formData.subscriptionType.type ? (
                                <p className="field-value">
                                    {t(`vendorRegistration.subscriptionTypes.${formData.subscriptionType?.type?.version_name}`)}
                                    {formData.subscriptionType?.type?.version_name !== "Free" && (
                                        <span className="discount-price">
                                            {formData.subscriptionType?.type?.pricing
                                                ? t('vendorRegistration.subscriptionTypes.monthly')
                                                : t('vendorRegistration.subscriptionTypes.yearly')} - ${formData.subscriptionType?.type?.pricing || formData.subscriptionType?.type?.pricing_yearly}
                                        </span>
                                    )}
                                </p>
                            ) : (
                                <p className="field-value">
                                    <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <h2 className="section-title">{t('previewDetails.sections.address')}</h2>
                <div className="details-card">
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.street')}</p>
                            <p className="field-value">{displayValue(formData.address.street, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.city')}</p>
                            <p className="field-value">{displayValue(formData.address.city, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.country')}</p>
                            <p className="field-value">{displayValue(formData.registrationDetails.phone_country?.name, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.region')}</p>
                            <p className="field-value">{displayValue(formData.address.region, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.zip')}</p>
                            <p className="field-value">{displayValue(formData.address.zip)}</p>
                        </div>
                    </div>
                </div>

                {/* Transaction Details Section with ID Card Fields */}
                <h2 className="section-title">{t('previewDetails.sections.transaction')}</h2>
                <div className="details-card">
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.accountHolder')}</p>
                            <p className="field-value">{displayValue(formData.accountDetails.accountname, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.bankName')}</p>
                            <p className="field-value">{displayValue(formData.accountDetails.bankname)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.accountNumber')}</p>
                            <p className="field-value">{displayValue(formData.accountDetails.iban)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.swiftCode')}</p>
                            <p className="field-value">{displayValue(formData.accountDetails.swift)}</p>
                        </div>
                    </div>
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.branchAddress')}</p>
                            <p className="field-value">{displayValue(formData.accountDetails.address, true)}</p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.paymentMethod')}</p>
                            <p className="field-value">
                                {formData.accountDetails.payment_method?.value
                                    ? t(`paymentMethods.${formData.accountDetails.payment_method.value}`)
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.currency')}</p>
                            <p className="field-value">
                                {formData.accountDetails.currency?.value
                                    ? t(`currencies.${formData.accountDetails.currency.value}`)
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.idCardType')}</p>
                            <p className="field-value">
                                {formData.accountDetails.idCardType
                                    ? getIdCardTypeLabel(formData.accountDetails.idCardType)
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                    </div>
                    <div className="details-grid">
                        <div className="grid-item">
                            <p className="field-label">{t('previewDetails.labels.idCardNumber')}</p>
                            <p className="field-value">
                                {formData.accountDetails.idCardNo
                                    ? formData.accountDetails.idCardNo
                                    : <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                }
                            </p>
                        </div>
                        <div className="grid-item" style={{ gridColumn: '2 / -1' }}>
                            <p className="field-label">{t('previewDetails.labels.proofDocument')}</p>
                            {formData.accountDetails.proof?.length > 0 ? (
                                <div className="file-preview-list">
                                    {formData.accountDetails.proof.map((file, index) => {
                                        const { icon, type } = getFileInfo(file.name);
                                        return (
                                            <div
                                                key={index}
                                                className="file-preview__thumbnail clickable-file"
                                                onClick={() => handleFileClick(file)}
                                                title={`Click to preview ${type}: ${file.name}`}
                                            >
                                                {icon}
                                                <span className="file-preview__filename">
                                                    {file.name}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="field-value">
                                    <span style={{ opacity: 0.6 }}>{t('previewDetails.labels.notProvided')}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                show={showPreview}
                onHide={handleClosePreview}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                backdrop="static"
                keyboard={false}
                className="file-preview-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-file me-2"></i>
                        {currentFile?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '0', minHeight: '400px' }}>
                    {renderFileContent()}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between w-100">
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                            {currentFileUrl && (
                                <a
                                    href={currentFileUrl}
                                    download={currentFile?.name}
                                    className="btn btn-outline-primary btn-sm me-2"
                                >
                                    <i className="fas fa-download me-1"></i>
                                    {t('previewDetails.labels.download')}
                                </a>
                            )}
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleClosePreview}
                            >
                                {t('previewDetails.labels.close')}
                            </button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
});

export default PreviewDetailsPage;